import { featureFlags } from '@seen/core/featureFlags'
import { compact } from 'lodash-es'
import { useMemo } from 'react'
import { useCurrentCarePlanURL } from '#src/hooks/useCurrentCarePlanURL.ts'
import { useCurrentPractitionerPerms } from '#src/hooks/useCurrentPractitionerPerms.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useNotesViewStore } from '#src/hooks/useNotesViewStore.ts'
import { usePortalStore } from '#src/hooks/usePortalStore.ts'

export type PatientNavigationItem = {
  label: string
  to: string
  value?: string
  indicator?: number
  // Path segment that keeps this item highlighted across sibling routes — e.g.
  // Notes stays active on every notes view while `to` points at just the
  // last-selected one.
  activeSegment?: string
}

export interface PatientNavigationItemsOptions {
  tasksIndicator?: number
}

export function usePatientNavigationItems(
  options?: PatientNavigationItemsOptions
): PatientNavigationItem[] {
  const currentCarePlanURL = useCurrentCarePlanURL().data
  const showAdminActions = usePortalStore((state) => state.showAdminActions)
  const { isAuditor } = useCurrentPractitionerPerms()
  const lastNotesView = useNotesViewStore((state) => state.lastNotesView)
  const facesheetImprovements = useFeatureFlag(
    featureFlags.facesheetImprovements
  )
  return useMemo(
    () =>
      compact([
        {
          label: 'Timeline',
          to: '',
          value: 'timeline',
        },
        showAdminActions &&
          !isAuditor && {
            label: 'Desks',
            to: 'desks',
            value: 'desks',
          },
        {
          label: 'Appointments',
          to: 'appointments',
        },
        {
          label: 'Care Plan',
          to: currentCarePlanURL,
          value: 'care-plans',
        },
        {
          // Target the last-selected notes view (browser-level pref, default
          // All) so clicking Notes on any participant reopens whichever view the
          // user was last on. `activeSegment` keeps the tab highlighted on every
          // notes view even though `to` points at only one.
          label: 'Notes',
          to: `notes/${lastNotesView}`,
          activeSegment: 'notes',
        },
        !isAuditor && {
          label: 'Tasks',
          to: 'tasks',
          indicator: options?.tasksIndicator,
        },
        {
          label: 'Orders',
          to: 'orders',
        },
        {
          label: 'Medications',
          to: 'medications',
        },
        {
          label: 'Documents',
          to: 'documents',
        },
        {
          label: 'SDRs',
          to: 'sdr',
        },
        {
          label: 'Validated Tools',
          to: 'validated-tools',
        },
        !isAuditor && {
          label: 'Coverage',
          to: 'coverage',
        },
        !isAuditor && {
          label: 'Admissions',
          to: 'admissions',
        },
        !isAuditor && {
          label: 'Incidents',
          to: 'incidents',
        },
        {
          label: 'Conditions',
          to: 'conditions',
        },
        facesheetImprovements && {
          label: 'Observations',
          to: 'observations',
        },
        {
          label: 'Immunizations',
          to: 'immunizations',
        },
        !isAuditor && {
          label: 'Memories',
          to: 'memories',
        },
      ]),
    [
      currentCarePlanURL,
      facesheetImprovements,
      isAuditor,
      lastNotesView,
      showAdminActions,
      options?.tasksIndicator,
    ]
  )
}
